import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  IconChevronLeft,
  IconChevronRight,
  IconHistoryOff,
  IconLeaf,
  IconPillFilled,
  IconPlus,
} from "@tabler/icons-react";
import MProfilePicker from "@/components/ui/atoms/MProfilePicker";
import MEmptyCard from "@/components/ui/organisms/MEmptyCard";
import MLoader from "@/components/ui/atoms/MLoader";
import { getMemberInfo } from "@/hooks/useMembers";
import { usePillbox } from "@/hooks/usePillbox";
import { MedicineItemType, MedicineStatus, PillboxItem } from "@/types/pillbox";
import {
  formatDisplayDate,
  formatFrequencyLine,
  formatMedicineTitle,
} from "./utils/formatMedicine";
import styles from "./PastMedications.module.css";

const PastMedications: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id || "";
  const { fetchLedger } = usePillbox(memberId);

  // Forwards the List/Schedule tab the landing screen was on before
  // navigating here (see PillboxLanding's quick-action onClick), so going
  // back restores it instead of always landing on the default Schedule tab.
  const handleBackToLanding = () => {
    const fromView = (
      location.state as { fromView?: "schedule" | "list" } | null
    )?.fromView;
    navigate("/pillbox", { state: { viewMode: fromView } });
  };

  const [items, setItems] = useState<PillboxItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!memberId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchLedger(MedicineStatus.STOPPED)
      .then((data) => {
        if (!cancelled) setItems(data);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [memberId, fetchLedger]);

  const handleAdd = () => navigate("/pillbox/add-medicine");
  const handleSelectItem = (item: PillboxItem) =>
    navigate(`/pillbox/${memberId}/${item.itemType}/${item.id}`);

  if (loading) {
    return (
      <div className={styles.loaderWrap}>
        <MLoader />
      </div>
    );
  }

  const isEmpty = items.length === 0;

  return (
    <div className={styles.root}>
      <div className={styles.headerCard}>
        <div className={styles.topBar}>
          <button
            type="button"
            className={styles.backButton}
            onClick={handleBackToLanding}
          >
            <IconChevronLeft size={16} />
            {t("common.back")}
          </button>
          <h1 className={styles.title}>
            {t("pillbox.pastMedicationsPage.title")}
          </h1>
          <div className={styles.iconSpacer} />
        </div>
        <div className={styles.switchMemberRow}>
          <MProfilePicker />
          <button
            type="button"
            className={styles.headerAddButton}
            onClick={handleAdd}
            aria-label={t("pillbox.add")}
          >
            <IconPlus size={20} stroke={2} />
          </button>
        </div>
        <p className={styles.subtitle}>
          {t(
            isEmpty
              ? "pillbox.pastMedicationsPage.subtitleEmpty"
              : "pillbox.pastMedicationsPage.subtitle"
          )}
        </p>
      </div>

      <div className={styles.body}>
        {isEmpty ? (
          <MEmptyCard
            className={styles.emptyCard}
            icon={<IconHistoryOff size={64} stroke={1.5} color="#AD95D1" />}
            primaryTitle={t("pillbox.pastMedicationsPage.emptyTitle")}
          />
        ) : (
          <>
            <span className={styles.countLabel}>
              {t("pillbox.pastMedicationsPage.count", { count: items.length })}
            </span>
            <div className={styles.list}>
              {items.map((item) => {
                const isSupplement =
                  item.itemType === MedicineItemType.SUPPLEMENT;
                return (
                  <button
                    type="button"
                    key={item.id}
                    className={styles.listItem}
                    onClick={() => handleSelectItem(item)}
                  >
                    <span className={styles.iconBox}>
                      {isSupplement ? (
                        <IconLeaf size={24} stroke={1.5} color="#474747" />
                      ) : (
                        <IconPillFilled size={24} color="#474747" />
                      )}
                    </span>
                    <span className={styles.itemContent}>
                      <span className={styles.itemName}>
                        {formatMedicineTitle(item)}
                      </span>
                      <span className={styles.itemFrequency}>
                        {formatFrequencyLine(item, item.recurrenceIntervalDays)}
                      </span>
                      {item.stoppedAt && (
                        <span className={styles.stoppedBadge}>
                          {t("pillbox.pastMedicationsPage.stoppedOn", {
                            date: formatDisplayDate(item.stoppedAt),
                          })}
                        </span>
                      )}
                    </span>
                    <IconChevronRight
                      size={24}
                      className={styles.trailingChevron}
                    />
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PastMedications;
